(() => {
  const BOOST_FALLBACK_WAIT = 1800;

  const isBoostSearchInput = (input) => Boolean(
    input?.classList.contains('boost-sd__search-widget-init-input')
    || input?.id?.startsWith('boost-sd__search-widget-init-input-')
    || input?.dataset.searchBox?.startsWith('boost-sd__')
    || input?.getAttribute('aria-owns')?.startsWith('boost-sd__')
  );

  const isBoostInstantSearchConfigured = () => (
    window.boostSDAppConfig?.themeInfo?.taeFeatures?.instantSearch === 'installed'
  );

  const bindHeaderSearchFallback = (form, index) => {
    const input = form.querySelector('[data-js-search-input]');
    const results = form.querySelector('[data-js-search-results]');
    if (!input || !results || input.dataset.fsSearchFallback === 'true' || isBoostSearchInput(input)) return;

    input.dataset.fsSearchFallback = 'true';
    results.setAttribute('aria-live', 'polite');
    const resultsId = results.id || `fs-predictive-results-${index}`;
    results.id = resultsId;
    input.setAttribute('aria-controls', resultsId);
    input.setAttribute('aria-expanded', 'false');

    const listenerController = new AbortController();
    let requestTimer;
    let activeRequest;

    const clearResults = () => {
      results.innerHTML = '';
      input.setAttribute('aria-expanded', 'false');
    };

    const releaseFallback = () => {
      window.clearTimeout(requestTimer);
      if (activeRequest) activeRequest.abort();
      listenerController.abort();
      clearResults();
      delete input.dataset.fsSearchFallback;
    };

    const ownershipObserver = new MutationObserver(() => {
      if (!isBoostSearchInput(input)) return;
      ownershipObserver.disconnect();
      releaseFallback();
    });

    ownershipObserver.observe(input, {
      attributes: true,
      attributeFilter: ['aria-owns', 'class', 'data-search-box', 'id']
    });

    input.addEventListener('input', () => {
      if (isBoostSearchInput(input)) {
        ownershipObserver.disconnect();
        releaseFallback();
        return;
      }

      window.clearTimeout(requestTimer);
      if (activeRequest) activeRequest.abort();

      const query = input.value.trim();
      if (query.length < 2) {
        clearResults();
        return;
      }

      requestTimer = window.setTimeout(async () => {
        activeRequest = new AbortController();
        const searchFields = window.KROWN?.settings?.predictive_search_fields;
        const fieldsParam = searchFields
          ? `&resources[options][fields]=${encodeURIComponent(searchFields)}`
          : '';
        const endpoint = window.KROWN?.settings?.routes?.predictive_search_url || '/search/suggest';

        try {
          const response = await fetch(
            `${endpoint}?q=${encodeURIComponent(query)}&section_id=helper-predictive-search${fieldsParam}`,
            { signal: activeRequest.signal }
          );
          if (!response.ok) throw new Error(`Predictive search: ${response.status}`);
          const html = await response.text();
          const parsed = new DOMParser().parseFromString(html, 'text/html');
          const section = parsed.querySelector('#shopify-section-helper-predictive-search');
          if (!section || input.value.trim() !== query || isBoostSearchInput(input)) return;
          results.innerHTML = section.innerHTML;
          input.setAttribute('aria-expanded', results.textContent.trim() ? 'true' : 'false');
        } catch (error) {
          if (error.name !== 'AbortError') clearResults();
        }
      }, 300);
    }, { signal: listenerController.signal });

    input.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') clearResults();
    }, { signal: listenerController.signal });
  };

  const initHeaderSearchFallback = () => {
    document.querySelectorAll('search-form').forEach((form, index) => {
      const input = form.querySelector('[data-js-search-input]');
      if (!input || isBoostSearchInput(input)) return;

      if (isBoostInstantSearchConfigured()) {
        window.setTimeout(() => bindHeaderSearchFallback(form, index), BOOST_FALLBACK_WAIT);
      } else {
        bindHeaderSearchFallback(form, index);
      }
    });
  };

  const translateBoostInterface = () => {
    document.querySelectorAll('.boost-sd__search-bar-submit').forEach((button) => {
      button.setAttribute('aria-label', 'Suchen');
      const label = button.querySelector('span');
      if (label?.textContent.trim() === 'Submit') label.textContent = 'Suchen';
    });

    document.querySelectorAll('[class*="boost-sd__button-text-x--close-style"]').forEach((button) => {
      button.setAttribute('aria-label', 'Schließen');
      button.setAttribute('title', 'Schließen');
      if (button.textContent.trim() === 'Close') button.textContent = 'Schließen';
    });

    document.querySelectorAll('[class*="boost-sd__button-text-x--clear-style"]').forEach((button) => {
      button.setAttribute('aria-label', 'Eingabe löschen');
      button.setAttribute('title', 'Eingabe löschen');
      if (button.textContent.trim() === 'Clear') button.textContent = 'Eingabe löschen';
    });

    document.querySelectorAll('.boost-sd__button-text-x--clear-input-search-form').forEach((button) => {
      if (button.dataset.fsTranslated === 'true') return;
      button.setAttribute('aria-label', 'Eingabe löschen');
      button.setAttribute('title', 'Eingabe löschen');
      button.innerHTML = '<span class="visually-hidden">Eingabe löschen</span>';
      button.dataset.fsTranslated = 'true';
    });

    document.querySelectorAll('.boost-sd__search-form-input, .boost-sd__search-widget-init-input, #boost-sd__search-bar-input').forEach((input) => {
      input.setAttribute('aria-label', 'Suche');
    });

    document.querySelectorAll('[aria-label]').forEach((element) => {
      const label = element.getAttribute('aria-label');
      if (!label) return;
      if (label === 'Product filter') element.setAttribute('aria-label', 'Produktfilter');
      if (label.startsWith('Filter by:')) {
        element.setAttribute('aria-label', label.replace('Filter by:', 'Filtern nach:'));
      }
      if (label.startsWith('Number of products:')) {
        element.setAttribute('aria-label', label.replace('Number of products:', 'Anzahl Produkte:'));
      }
    });

    document.querySelectorAll('.boost-sd__toolbar-item--product-count, .boost-sd__toolbar-item').forEach((element) => {
      const text = element.textContent.trim();
      if (/^\d+ products$/i.test(text)) {
        element.textContent = text.replace(/products/i, 'Produkte');
      }
      if (/^1 product$/i.test(text)) {
        element.textContent = '1 Produkt';
      }
    });

    const productPanel = Array.from(document.querySelectorAll('.boost-sd__search-result-toolbar-panel'))
      .find((element) => /^Produkte\s*\(\d+\)/.test(element.textContent.trim()));
    const productCountMatch = productPanel?.textContent.trim().match(/\((\d+)\)/);
    const productResultCount = productCountMatch ? Number(productCountMatch[1]) : null;

    document.querySelectorAll('.boost-sd-container-search-page *, .boost-sd__search-bar-wrapper *').forEach((element) => {
      if (element.children.length > 0) return;
      const text = element.textContent.trim();
      if (text === 'SALE') element.textContent = 'ANGEBOT';
      if (text === 'SOLD OUT') element.textContent = 'AUSVERKAUFT';
      if (/^Zeigen \d+ Ergebnis/.test(text)) element.dataset.fsBoostResultCount = 'true';
      if (element.dataset.fsBoostResultCount === 'true' && productResultCount !== null) {
        const resultLabel = productResultCount === 1 ? '1 Ergebnis' : `${productResultCount} Ergebnisse`;
        if (element.textContent !== resultLabel) element.textContent = resultLabel;
      }
    });
  };

  const run = () => {
    initHeaderSearchFallback();
    translateBoostInterface();
    const target = document.body;
    if (!target) return;
    new MutationObserver(translateBoostInterface).observe(target, {
      childList: true,
      characterData: true,
      subtree: true
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run, { once: true });
  } else {
    run();
  }
})();
